import {
    DEFAULT_SPROUT_STREAM_MODEL_ID,
    DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
    EXPERIMENTAL_MEMBER_AGENT_REFS,
    getSproutStreamReasoningEffortsForModel,
    MEMBER_AGENTS,
    SPROUT_AGENT,
    SPROUT_STREAM_MODEL_IDS,
    type SproutStreamModelId,
    type SproutStreamReasoningEffort,
} from '@fruitful/llm-service-shared';
import { useState } from 'react';
import {
    BodyMediumRegular,
    ChevronDownIcon,
    ChevronUpIcon,
    FootnoteRegular,
    Icon,
    IconButton,
    RadioInputRow,
    SettingsIcon,
    Sheet,
    XStack,
    YStack,
} from 'smoothie';

import { useShowSproutDevTools } from '../../hooks/useShowSproutDevTools';

interface SproutOptionsButtonProps {
    modelId: SproutStreamModelId;
    reasoningEffort: SproutStreamReasoningEffort;
    agentKey: string;
    onModelIdChange: (modelId: SproutStreamModelId) => void;
    onReasoningEffortChange: (
        reasoningEffort: SproutStreamReasoningEffort,
    ) => void;
    onAgentKeyChange: (agentKey: string) => void;
}

export const SproutStreamSelectionHint = ({
    modelId,
    reasoningEffort,
    agentKey,
}: {
    modelId: SproutStreamModelId;
    reasoningEffort: SproutStreamReasoningEffort;
    agentKey?: string;
}) => {
    const showDevTools = useShowSproutDevTools();
    if (!showDevTools) {
        return null;
    }

    const agentLabel =
        agentKey != null &&
        agentKey !== '' &&
        agentKey !== SPROUT_AGENT.agentKey
            ? `${agentKey} · `
            : '';

    return (
        <FootnoteRegular color="$gray1000" textAlign="center" paddingTop="$2">
            {`${agentLabel}${modelId} · ${reasoningEffort}`}
        </FootnoteRegular>
    );
};

export const SproutOptionsButton = ({
    modelId,
    reasoningEffort,
    agentKey,
    onModelIdChange,
    onReasoningEffortChange,
    onAgentKeyChange,
}: SproutOptionsButtonProps) => {
    const showDevTools = useShowSproutDevTools();
    const [isOpen, setIsOpen] = useState(false);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

    if (!showDevTools) {
        return null;
    }

    const selectableAgents = [
        ...MEMBER_AGENTS,
        ...EXPERIMENTAL_MEMBER_AGENT_REFS,
    ];

    return (
        <>
            <IconButton
                variant="secondary"
                icon={SettingsIcon}
                onPress={() => {
                    setIsOpen(true);
                }}
            />
            <Sheet
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
                title="Sprout options"
                showCloseButton
            >
                <YStack gap={16}>
                    {selectableAgents.length > 1 ? (
                        <YStack gap={8}>
                            <BodyMediumRegular>Agent</BodyMediumRegular>
                            {selectableAgents.map((option) => (
                                <RadioInputRow
                                    key={option.agentKey}
                                    label={option.name}
                                    isSelected={agentKey === option.agentKey}
                                    onPress={() => {
                                        onAgentKeyChange(option.agentKey);
                                    }}
                                />
                            ))}
                        </YStack>
                    ) : null}
                    <YStack gap={8}>
                        <XStack
                            alignItems="center"
                            justifyContent="space-between"
                            paddingVertical={8}
                            cursor="pointer"
                            onPress={() => {
                                setIsAdvancedOpen((wasOpen) => !wasOpen);
                            }}
                        >
                            <BodyMediumRegular>
                                Advanced (Beta)
                            </BodyMediumRegular>
                            <Icon
                                icon={
                                    isAdvancedOpen
                                        ? ChevronUpIcon
                                        : ChevronDownIcon
                                }
                                size={16}
                                color="$gray1200"
                            />
                        </XStack>
                        {isAdvancedOpen ? (
                            <YStack gap={16}>
                                <YStack gap={8}>
                                    <BodyMediumRegular>Model</BodyMediumRegular>
                                    {SPROUT_STREAM_MODEL_IDS.map((option) => (
                                        <RadioInputRow
                                            key={option}
                                            label={option}
                                            isSelected={modelId === option}
                                            onPress={() => {
                                                onModelIdChange(option);
                                            }}
                                        />
                                    ))}
                                </YStack>
                                <YStack gap={8}>
                                    <BodyMediumRegular>
                                        Reasoning effort
                                    </BodyMediumRegular>
                                    {getSproutStreamReasoningEffortsForModel(
                                        modelId,
                                    ).map((option) => (
                                        <RadioInputRow
                                            key={option}
                                            label={option}
                                            isSelected={
                                                reasoningEffort === option
                                            }
                                            onPress={() => {
                                                onReasoningEffortChange(option);
                                            }}
                                        />
                                    ))}
                                </YStack>
                                <BodyMediumRegular>
                                    {`Applies to the next message in this chat. Default is ${DEFAULT_SPROUT_STREAM_MODEL_ID} · ${DEFAULT_SPROUT_STREAM_REASONING_EFFORT}.`}
                                </BodyMediumRegular>
                            </YStack>
                        ) : null}
                    </YStack>
                </YStack>
            </Sheet>
        </>
    );
};
